import { ApiError } from 'utils'
import { ZodError, z } from 'zod'

import { CropType } from '../ag-concepts'
import { OperationLevelPermission } from '../auth-claims/permissions'
import { defineApi } from '../http'
import { type Task, taskSchema } from '../pangea'
import { SharingConnectionType } from '../partner-access/connection-types'
import { manyItems } from '../shared'
import { controllableApplicationTypes } from '../stitch'
import { GeoSpatialFileType } from './geo-spatial-file-type'
import type { GuidancePlanSummary, LineSet } from './guidance'
import type { Locutus } from './index'

export const getPlanningCffsQueryKey = 'get-planning-cffs'

export type FrontendPlan = {
  id?: string
  fieldId: string
  name?: string
  task?: Task
  date:
    | { type: 'recurring' }
    | {
        type: 'expiring'
        month: number
        year: number
      }
  crop?: CropType
  customCrop?: string
  boundary?: Pick<Locutus.BoundaryPlanAsset, 'id' | 'name'>
  prescriptions: (Pick<Locutus.RxPlanAsset, 'id' | 'name'> &
    Pick<Locutus.RxPlanAsset['data'], 'type'>)[]
  guidance?: Plan['guidance']
}

const initialPlan: Readonly<FrontendPlan> = Object.freeze({
  id: undefined,
  fieldId: '',
  name: undefined,
  task: undefined,
  date: { type: 'recurring' },
  crop: undefined,
  customCrop: undefined,
  boundary: undefined,
  prescriptions: [],
  guidance: undefined
} satisfies FrontendPlan)

export const getInitialPlan = () => structuredClone(initialPlan)

export enum PlanAsset {
  LiquidTankMix = 'liquidTankMix',
  GranularTankMix = 'granularTankMix',
  HybridInput = 'hybridInput',
  Prescription = 'rx',
  Boundary = 'boundary',
  GuidancePrimitive = 'guidancePrimitive',
  GuidancePlan = 'guidancePlan',
  LineSet = 'lineSet'
}

export const actorSchema = z.discriminatedUnion('actor', [
  z.object({ actor: z.literal('aurora'), onBehalfOf: z.string() }),
  z.object({ actor: z.literal('user'), username: z.string() }),
  z.object({ actor: z.literal('partner'), partnerId: z.string() })
])

export type Actor = z.infer<typeof actorSchema>

export const sharedAssetSchema = z.object({
  orgCode: z.string(),
  type: z.enum(PlanAsset),
  name: z.string(),
  id: z.uuid(),
  crop: z.enum(CropType).optional(),
  customCrop: z.string().optional(),
  /**
   * Optional partner id, if the asset was imported from a partner connection (e.g., Ops Center)
   */
  partnerSourceId: z.string().optional(),
  createdAt: z.coerce.date(),
  createdBy: actorSchema.optional(),
  /**
   * Assets are immutable, so we do not actually delete them, just mark them as deleted.
   * This is used to filter out deleted assets when listing them.
   *
   * The underlying asset remains in the database and remains available to DBMs that already
   * have references to it.
   */
  isDeleted: z.boolean().default(false)
})

/**
 * Creates a schema for a plan asset that concerns a particular product type,
 * such as tank mix or hybrid.
 *
 * The data field is left open for the specific asset type to define.
 */
export const productAssetSchema = <T>(data: z.ZodSchema<T>) => sharedAssetSchema.extend({ data })

export const lineageIdSchema = z.uuid().brand<'LineageId'>()

export type LineageId = z.infer<typeof lineageIdSchema>

export const rootLineageId = (rootVersionId: string) => rootVersionId as LineageId

/**
 * Creates a schema for a plan asset that concerns a particular field,
 * such as a prescription or boundary.
 */
export const fieldAssetSchema = <T>(data: z.ZodSchema<T>) =>
  sharedAssetSchema.extend({
    data,
    lineageId: lineageIdSchema,
    parentVersionId: dynamodbAssetIdSchema.optional(),
    fieldId: dynamodbAssetIdSchema,
    /**
     * The original upload source type of the asset. If missing, it was uploaded prior to
     * tracking this field.
     */
    sourceType: z.enum(GeoSpatialFileType).optional()
  })

/**
 * Since geojson is large, it will be stored in s3 and this will be the id of the object
 * stored at path `/:orgCode/:assetType/:id`.
 */
const s3AssetIdSchema = z.uuid()

/**
 * An opaque asset id to use for cross-referencing plan details.
 * Internal to Panorama, subject to change from uuid to some other opaque string.
 */
const dynamodbAssetIdSchema = z.uuid()

/**
 * A reference to a plan asset that is stored in DynamoDB.
 * This is used for tank mixes, hybrid inputs, and line sets, which are stored in DynamoDB.
 */
const dynamodbAssetReferenceSchema = z.object({
  name: z.string(),
  id: dynamodbAssetIdSchema
})

/**
 * A reference to a plan asset that is stored in S3.
 * This is used for prescriptions, boundaries, and guidance plans, which are stored in S3.
 */
const s3ObjectReferenceSchema = z.object({
  name: z.string(),
  id: s3AssetIdSchema
})

/**
 * The parts that make up a CFF (client, farm, and field names)
 */
export const baseCffSchema = z.object({
  client: z.string(),
  farm: z.string(),
  field: z.string()
})

/**
 * A configuration for a CFF, which is a field that will have tasks planned for it.
 */
export const cffSchema = baseCffSchema.and(
  z.object({
    id: dynamodbAssetIdSchema,
    orgCode: z.string(),
    createdBy: actorSchema.optional()
  })
)

/**
 * A plan for a field, optionally but likely scoped to a given season.
 * Contains all relevant plan assets attached to the task.
 */
export const planSchema = z.object({
  id: dynamodbAssetIdSchema,
  orgCode: z.string(),
  /**
   * The id of the CFF name that this plan is attached to, used for cross-referencing.
   */
  fieldId: dynamodbAssetIdSchema,
  /**
   * Human-readable name for the planned task, entered by user.
   * Defaults to the name of the task type.
   */
  name: z.string(),
  /**
   * The task that will be performed.
   * This is a reference to the task schema in stitch.
   */
  task: taskSchema,
  /**
   * When the task will occur.
   * If the task is recurring, there is no date.
   * If the task is expiring, there is a year and month.
   */
  date: z.discriminatedUnion('type', [
    z.object({
      type: z.literal('expiring'),
      year: z.number(),
      month: z.number()
    }),
    z.object({ type: z.literal('recurring') })
  ]),
  /**
   * Optional because some activities may be done without a specific crop in mind, like
   * tillage or burn-down applications.
   */
  crop: z.enum(CropType).optional(),
  customCrop: z.string().optional(),
  boundary: s3ObjectReferenceSchema.optional(),
  prescriptions: z.array(s3ObjectReferenceSchema.extend({ type: controllableApplicationTypes })),
  tankMixes: z.array(dynamodbAssetReferenceSchema),
  hybridInputs: z.array(dynamodbAssetReferenceSchema),
  createdAt: z.coerce.date().optional(),
  createdBy: actorSchema.optional(),
  lastModified: z.coerce.date(),
  lastModifiedBy: actorSchema.optional(),
  guidance: z
    .object({
      lineSets: z.array(dynamodbAssetReferenceSchema).min(1),
      plan: s3ObjectReferenceSchema
    })
    .optional()
})

export type Plan = z.infer<typeof planSchema>

/**
 * The listed guidance assets that a plan's guidance references resolve against.
 */
export type ListedGuidanceAssets = {
  guidancePlans: { id: string; data: GuidancePlanSummary }[]
  lineSets: { id: string; data: LineSet }[]
}

/**
 * The ids of the guidance primitives a plan uses: its line sets' lines and the
 * lines its guidance plan assigns to regions. A referenced asset missing from
 * `assets` contributes nothing.
 */
export const getPlanGuidancePrimitiveIds = ({
  plan,
  assets
}: {
  plan: Pick<Plan, 'guidance'> | undefined
  assets: ListedGuidanceAssets
}): string[] => {
  const guidance = plan?.guidance
  if (guidance === undefined) return []

  const lineSetIds = new Set(guidance.lineSets.map(({ id }) => id))
  const lineSetPrimitives = assets.lineSets
    .filter(({ id }) => lineSetIds.has(id))
    .flatMap(({ data }) => data.primitives)
  const guidancePlan = assets.guidancePlans.find(({ id }) => id === guidance.plan.id)
  const planPrimitives =
    guidancePlan === undefined ? [] : guidancePlan.data.infillRegions.map(({ infill }) => infill)
  return [...lineSetPrimitives, ...planPrimitives].map(({ id }) => id)
}

export const filterGuidancePrimitivesForPlan = <T extends { id: string }>({
  guidancePrimitives,
  assets,
  plans,
  planId
}: {
  guidancePrimitives: T[]
  assets: ListedGuidanceAssets
  plans: Plan[]
  planId: string | null
}): T[] => {
  if (planId === null) {
    const referencedGuidanceIds = new Set(
      plans.flatMap((plan) => getPlanGuidancePrimitiveIds({ plan, assets }))
    )
    return guidancePrimitives.filter((primitive) => !referencedGuidanceIds.has(primitive.id))
  }

  const plan = plans.find((candidate) => candidate.id === planId)
  const planGuidanceIds = new Set(getPlanGuidancePrimitiveIds({ plan, assets }))
  return guidancePrimitives.filter((primitive) => planGuidanceIds.has(primitive.id))
}

const basePlanningCffPath = ({ orgCode }: { orgCode: string }) => `/locutus/${orgCode}/cff`

/**
 * Api for getting all CFFs in an operation
 */
export const getPlanningCffsApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string()
  }),
  pathFor: basePlanningCffPath,
  name: 'GetPlanningFields',
  description: 'Retrieve all configured fields (client/farm/field combinations) for planning',
  requiredPermissions: OperationLevelPermission.ViewPlans,
  responseSchema: manyItems(cffSchema),
  // Must match createPlanningCffsApi's invalidateQueries so registering a
  // field refreshes every consumer of the CFF list.
  queryKey: ({ orgCode }) => [getPlanningCffsQueryKey, orgCode],
  gatewayPath: '/:orgCode/cff' as const
})

export const getPlanningCffsForPartnerApi = defineApi({
  ...getPlanningCffsApi,
  name: 'GetPlanningFieldsForPartner',
  description: 'Retrieve all configured fields for external partner access',
  gatewayPath: '/export/planning/:orgCode/fields' as const,
  connectionTypes: [
    SharingConnectionType.AgronomicAdvisor,
    SharingConnectionType.ExportFieldPlanningData,
    SharingConnectionType.BidirectionalFieldPlanningData,
    SharingConnectionType.ImportFieldPlanningData // needed for partners to see the field ids the partner should use when posting other data
  ]
})

/**
 * Api for creating new CFFs for an operation
 */
export const createPlanningCffsApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string()
  }),
  pathFor: basePlanningCffPath,
  name: 'CreatePlanningFields',
  description: 'Create new field configurations (client/farm/field combinations) for planning',
  requiredPermissions: OperationLevelPermission.ManagePlans,
  method: 'POST',
  gatewayPath: '/:orgCode/cff' as const,
  requestBodySchema: z.object({
    fields: z.array(baseCffSchema)
  }),
  responseSchema: z.void(),
  invalidateQueries: ({ orgCode }) => [getPlanningCffsQueryKey, orgCode],
  supportsCodec: true
})

export const createPlanningCffPartnerApi = defineApi({
  ...createPlanningCffsApi,
  name: 'CreatePlanningFieldsForPartner',
  description: 'Create new field configurations for external partner access',
  gatewayPath: '/import/planning/:orgCode/fields' as const,
  connectionTypes: [
    SharingConnectionType.ImportFieldPlanningData,
    SharingConnectionType.BidirectionalFieldPlanningData,
    SharingConnectionType.AgronomicAdvisor
  ]
})

/**
 * Api for deleting a CFF from an operation
 */
export const deletePlanningCffApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string(),
    fieldId: z.string()
  }),
  pathFor: ({ orgCode, fieldId }) => `${basePlanningCffPath({ orgCode })}/${fieldId}`,
  name: 'DeletePlanningField',
  description: 'Delete a configured field from the planning system',
  requiredPermissions: OperationLevelPermission.ManagePlans,
  method: 'DELETE',
  gatewayPath: '/:orgCode/cff/:fieldId' as const,
  responseSchema: z.void()
})

const validImportFile = z
  .instanceof(File)
  .refine((file) => ['.xlsx', '.csv', '.2020'].some((ext) => file.name.endsWith(ext)), {
    message: 'File must be a .xlsx, .csv, or .2020 account config file'
  })

/**
 * Api for creating CFFs from an Excel, CSV, or account config 2020 file
 */
export const convertFileToCffAPI = defineApi({
  pathSchema: z.object({ orgCode: z.string() }),
  pathFor: (p) => `/locutus/cff/convert/${p.orgCode}`,
  name: 'ImportFieldsFromFile',
  description:
    'Import field configurations from CSV, Excel, or 2020 account config file and optionally persist them',
  requiredPermissions: OperationLevelPermission.ManagePlans,
  method: 'POST',
  gatewayPath: '/cff/convert/:orgCode' as const,
  requestBodySchema: z.object({
    file: validImportFile
  }),
  querySchema: z.object({
    l: z.string().optional()
  }),
  requestBodyEncoding: 'form-data',
  responseSchema: manyItems(baseCffSchema),
  errorMap: async (error) => {
    if (error instanceof ApiError) {
      return new Error(error.responseText)
    }
    if (error instanceof ZodError) return new Error(z.prettifyError(error))
    return error
  },
  invalidateQueries: ({ orgCode }) => [getPlanningCffsQueryKey, orgCode]
})
